import _ from 'lodash';
import { history } from 'umi';

import { bindServices } from '@/services/api';

const api = bindServices('tasks');
import { CoreEnums } from '@/utils/domain/constants';
import { toPaginate, translate } from '@/utils/infrastructure';
import { status } from '@/utils/infrastructure/utilities';
import CoreQueries from '@/utils/input/queries';
import { runTaskCommand } from './service';

const meta = {
  page: 1,
  limit: 5,
  count: 0,
  totalPages: 0,
};

const initialState = {
  data: [],
  filtersConfig: [],
  applicantUser: [],
  selectedTask: {},
  showDetail: false,
  mapStatus: status.taskStatus,
  executedCommand: null,
  pagination: meta,
  adFilters: [],
  cardAction: null,
};

const Model = {
  namespace: 'tasks',
  state: initialState,
  effects: {
    *fetchTasks({ payload }, { call, put }) {
      try {
        const { taskListType, taskStatus, body, readPriority, ...params } = payload;
        const res = yield call(api.getAll, {
          route: `tasks/paged/${taskListType}/${taskStatus}`,
          params: { ...params, ...CoreQueries.Tasks.paged },
          readPriority,
          body,
        });

        yield put({
          type: 'queryTasks',
          payload: {
            ...res,
            ..._.pick(payload, ['page', 'limit']),
          },
        });
      } catch (e) {}
    },

    *createList({ payload }, { call, put }) {
      try {
        yield call(payload.id ? api.update : api.create, { route: 'tasks/list', data: payload });
        yield put({ type: 'global/tasksCountPending', payload: { readPriority: true } });
        yield put({
          type: 'queryRunCommand',
          payload: { command: CoreEnums.TaskListType.Custom },
        });

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'success',
            message: translate(payload.id ? 'tasks.list.updated' : 'tasks.list.created', {
              name: payload.title,
            }),
          },
        });
      } catch (e) {}
    },

    *removeCustomList({ payload }, { call, put }) {
      try {
        yield call(api.apiRoute, { route: `tasks/list/${payload.id}`, method: 'delete' });
        yield put({ type: 'global/tasksCountPending', payload: { readPriority: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'success',
            message: translate('tasks.list.removed', { name: payload.title }),
          },
        });

        const { taskListId } = history.location.query || {};
        if (payload.id === taskListId) history.replace('/tasks');
      } catch (e) {}
    },

    *setProperty({ payload }, { call, put }) {
      try {
        const { route, taskId, value, rowIndex } = payload;
        yield put({ type: 'global/isCmdRunning', payload: { route, rowIndex } });
        yield call(api.update, { route: `tasks/${route}/${taskId}/${value}`, method: 'post' });
        yield put({ type: 'global/queryCountPending', payload });
        yield put({ type: 'queryProperty', payload });
      } catch (e) {
      } finally {
        yield put({ type: 'global/isCmdRunning' });
      }
    },

    *moveTask({ payload }, { call, put }) {
      try {
        yield put({
          type: 'global/isCmdRunning',
          payload: _.pick(payload, ['command', 'rowIndex']),
        });

        yield call(api.update, {
          route: `tasks/moveToList/${payload.taskId}/${payload.listId}`,
          method: 'post',
        });

        yield put({ type: 'queryRunCommand', payload });
      } catch (e) {
      } finally {
        yield put({ type: 'global/isCmdRunning' });
      }
    },

    *addToMyDay({ payload }, { call, put }) {
      try {
        yield put({
          type: 'global/isCmdRunning',
          payload: _.pick(payload, ['command', 'rowIndex']),
        });

        yield call(api.update, { route: `tasks/addToMyDay/${payload.taskId}`, method: 'post' });
        yield put({ type: 'global/queryCountPending', payload });
        yield put({ type: 'queryRunCommand', payload });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'success',
            message: translate('tasks.task.toMayDay', { name: payload.title }),
          },
        });
      } catch (e) {
      } finally {
        yield put({ type: 'global/isCmdRunning' });
      }
    },

    *updateTaskSchedule({ payload }, { call, put }) {
      try {
        yield put({
          type: 'global/isCmdRunning',
          payload: _.pick(payload, ['command', 'rowIndex']),
        });

        const { taskId, name, value } = payload;
        yield call(api.update, {
          route: `tasks/change${_.capitalize(name)}/${taskId}?${name}=${value}`,
          method: 'post',
        });
        yield put({ type: 'global/tasksCountPending', payload: { readPriority: true } });
        yield put({ type: 'queryTaskSchedule', payload });
      } catch (e) {
      } finally {
        yield put({ type: 'global/isCmdRunning' });
      }
    },

    // Replace collaborators (JsonData items); optimistic `queryRelatedUsers` before API call.
    *changeRelatedUsers({ payload }, { call, put }) {
      try {
        yield put({
          type: 'global/isCmdRunning',
          payload: { route: 'changeRelatedUsers', rowIndex: payload.rowIndex },
        });

        const { taskId, items } = payload;
        yield put({ type: 'queryRelatedUsers', payload });
        yield call(api.create, {
          route: `tasks/changeRelatedUsers/${taskId}`,
          data: { items },
        });
      } catch (e) {
      } finally {
        yield put({ type: 'global/isCmdRunning' });
      }
    },

    *addNewTask({ payload }, { call, put }) {
      try {
        const { listId, ...data } = payload;
        const route = ['tasks', listId && `?listId=${listId}`].filter(Boolean).join('');
        yield call(api.create, { route, data });
        yield put({ type: 'global/tasksCountPending', payload: { readPriority: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'success',
            message: translate('tasks.task.created', { name: payload.name }),
          },
        });

        yield put({
          type: 'queryRunCommand',
          payload: { command: CoreEnums.TaskListType.Custom },
        });
      } catch (e) {}
    },

    *removeTask({ payload }, { call, put }) {
      try {
        yield put({
          type: 'global/isCmdRunning',
          payload: _.pick(payload, ['command', 'rowIndex']),
        });
        yield call(api.apiRoute, { route: `tasks/${payload.taskId}`, method: 'delete' });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'success',
            message: translate('tasks.task.removed', { name: payload.name }),
          },
        });

        yield put({ type: 'queryRunCommand', payload });
      } catch (e) {
      } finally {
        yield put({ type: 'global/isCmdRunning' });
      }
    },

    *runCommand({ payload }, { call, put }) {
      try {
        yield put({
          type: 'global/isCmdRunning',
          payload: _.pick(payload, ['command', 'rowIndex']),
        });
        yield call(runTaskCommand, payload);
        yield put({ type: 'queryRunCommand', payload });
      } catch (e) {
        yield put({
          type: 'global/showNotifications',
          payload: { visible: true, type: 'error' },
        });
      } finally {
        yield put({ type: 'global/isCmdRunning' });
      }
    },

    *filterBy({ payload }, { put }) {
      yield put({ type: 'queryFilters', payload });
    },

    *filterConfig({ payload }, { put }) {
      yield put({ type: 'queryFilterConfig', payload });
    },

    *execTaskCommand({ payload }, { put }) {
      yield put({ type: 'queryTaskCommand', payload });
    },

    *toggleCreateListModal({ payload }, { put }) {
      yield put({ type: 'queryCreateListModal', payload });
    },

    *cleanup(__, { put }) {
      yield put({ type: 'resetState' });
    },
  },
  reducers: {
    queryTasks(state, { payload }) {
      return {
        ...state,
        executedCommand: null,
        data: payload.items || [],
        pagination: { ...state.pagination, ...toPaginate(payload) },
      };
    },

    queryCreateList(state) {
      return {
        ...state,
        executedCommand: CoreEnums.TaskListType.Custom,
      };
    },

    queryProperty(state, { payload }) {
      const data = [...state.data];

      Object.assign(data, {
        [payload.rowIndex]: {
          ...data[payload.rowIndex],
          ...(payload.route === 'changeFavorite' && { favorite: payload.value }),
          ...(payload.route === 'changeImportance' && { importance: payload.value }),
          ...(payload.route === 'changeStatus' && { status: payload.value }),
        },
      });

      return {
        ...state,
        ...(payload.isChanged && { executedCommand: payload.listType }),
        data,
      };
    },

    queryTaskSchedule(state, { payload }) {
      const data = [...state.data];
      const index = data.findIndex((o) => o.id === payload.taskId);

      if (index !== -1) {
        Object.assign(data, {
          [index]: {
            ...data[index],
            [payload.name]: payload.value,
          },
        });
      }

      return { ...state, data };
    },

    // Map saved JsonData items to `relatedUserTasks` (id mirrors userId until refetch).
    queryRelatedUsers(state, { payload }) {
      const data = [...state.data];
      const index =
        typeof payload.rowIndex === 'number'
          ? payload.rowIndex
          : data.findIndex((o) => o.id === payload.taskId);

      if (index === -1 || !data[index]) return state;

      Object.assign(data, {
        [index]: {
          ...data[index],
          relatedUserTasks: (payload.items || []).map((o) => ({
            id: o.id,
            userId: o.id,
            userFullName: o.text,
          })),
        },
      });

      return { ...state, data };
    },

    queryRunCommand(state, { payload }) {
      return {
        ...state,
        executedCommand: payload?.command,
      };
    },

    queryFilterConfig(state, { payload }) {
      return {
        ...state,
        filtersConfig: payload,
      };
    },

    queryFilters(state, { payload }) {
      return {
        ...state,
        adFilters: payload,
      };
    },

    queryTaskCommand(state, { payload }) {
      return {
        ...state,
        cardAction: payload,
      };
    },

    taskDetail(state, { payload }) {
      return {
        ...state,
        selectedTask: payload.item ?? {},
        showDetail: payload.show,
      };
    },

    queryCreateListModal(state, { payload }) {
      return {
        ...state,
        showCreateListModal: payload,
      };
    },

    resetState() {
      return initialState;
    },
  },
};

export default Model;
